import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, IconButton, CircularProgress } from "@mui/material";
import { Icon } from "@iconify/react";
import { toast } from "sonner";

const ImageUploader = ({
  value,
  initialUrl = null,
  onChange,
  size = 200,
  disabled = false,
}) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  const hasUserSelectedRef = useRef(false);
  const objectUrlRef = useRef(null);

  /* --------------------------------
     Apply INITIAL server image ONCE
  --------------------------------- */
  useEffect(() => {
    if (
      !hasUserSelectedRef.current &&
      !value &&
      initialUrl &&
      !preview
    ) {
      setImageLoading(true);
      setPreview(initialUrl);

    }
  }, [initialUrl, value, preview]);

  /* --------------------------------
     Apply LOCAL file preview
  --------------------------------- */
  useEffect(() => {
    if (!value) return;

    hasUserSelectedRef.current = true;

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }

    const objectUrl = URL.createObjectURL(value);
    objectUrlRef.current = objectUrl;
    setImageLoading(true);
    setPreview(objectUrl);
  }, [value]);

  /* --------------------------------
     Cleanup on unmount
  --------------------------------- */
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    const maxSize = 5 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      toast.error("Only PNG, JPEG, or JPG files are allowed");
      e.target.value = "";
      return;
    }

    if (file.size > maxSize) {
      toast.error("File size must be less than 5MB");
      e.target.value = "";
      return;
    }

    onChange?.(file);
    e.target.value = "";
  };

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <input
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        ref={inputRef}
        hidden
        disabled={disabled}
        onChange={handleFileChange}
      />

      <Avatar
        src={preview || ""}
        variant="rounded"
        onClick={() => !disabled && inputRef.current?.click()}
        sx={{
          width: size,
          height: size,
          borderRadius: "10px",
          cursor: disabled ? "not-allowed" : "pointer",
          border: preview ? "none" : "1px dashed #5751E9",
          bgcolor: "#F4F5FF",
        }}
      >
        {imageLoading ? (
          <CircularProgress size={32} />
        ) : !preview ? (
          <Icon
            icon="mdi:image-outline"
            width="40"
            height="40"
            color="#5751E9"
          />
        ) : null}
      </Avatar>

      {!disabled && (
        <IconButton
          onClick={() => inputRef.current?.click()}
          sx={{
            position: "absolute",
            bottom: -8,
            right: -8,
            width: 30,
            height: 30,
            bgcolor: "#5751E9",
            "&:hover": { bgcolor: "#4b45d1" },
          }}
        >
          <Icon icon="mdi:pencil" width="18" height="18" color="#fff" />
        </IconButton>
      )}
    </Box>
  );
};

export default ImageUploader;
